import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Pagination,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from "@mui/icons-material/FilterList"; // ✅ filter icon
import ExportDropdown from "../../../../components/ExportDropdown.js";
import CustomText, { textStyles } from "../../../../components/CustomText.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../../components/BaseUrl.js";
import { showUnderDevelopment } from "../../../../components/Alert.js";
import TruncatedCell from "../../../../components/TruncatedCell.js";
import document1 from "../../../../assets/document1.svg";
import document2 from "../../../../assets/document2.svg";
import empty from "../../../../assets/empty.png";
import StatusDropdown from "../../../../components/StatusDropdown.js";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { formatCalendarDateForDisplay } from "../../../../components/dateTimeUtils";

// ---- shared styles
const headerStyle = {
  color: "#44546F",
  fontSize: "15px",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
};

const dataStyle = {
  color: "#172B4D",
  fontSize: "13px",
  fontWeight: 400,
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
};

// ---- helpers
const formatDate = (iso) => {
  const formatted = formatCalendarDateForDisplay(iso, { locale: "en-GB" });
  return formatted === "—" ? "-" : formatted;
};

const fileTypeIcon = (fileType) => {
  const ft = (fileType || "").toLowerCase();
  if (ft.includes("pdf")) return document1;
  return document2;
};

export default function WorkerDocument({workerId}) {
  const { t } = useTranslation();

  // ---- state
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false); // ✅
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortBy, setSortBy] = useState("uploaded_at");
const [sortOrder, setSortOrder] = useState("desc"); // or "asc"

const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
const [viewRow, setViewRow] = useState(null);
  const [filters, setFilters] = useState({
    category: "",
    startDate: "",
    endDate: "",
  });
const handleApplyFilters = (newFilters) => {
  console.log("📌 Applying new filters:", newFilters);
  setFilters(newFilters);
  setPage(1);

  // ✅ Check if any filter is applied
  const hasFilters =
    newFilters.category ||
    newFilters.startDate ||
    newFilters.endDate;
  setIsFilterApplied(Boolean(hasFilters));
};
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  useEffect(() => {
  
    const fetchDocs = async () => {
  if (!decryptedToken) return;

  if (rows.length === 0) setLoading(true);
  else setTableLoading(true);

  try {
    const queryParams = new URLSearchParams({
      limit,
      page,
      search,
        sort_by: sortBy,
  sort_order: sortOrder,
      ...(filters.category && { category: filters.category }),
      ...(filters.startDate && { date_from: filters.startDate }), // 👈 match backend param
      ...(filters.endDate && { date_to: filters.endDate }),       // 👈 match backend param
    });

    console.log("🔎 Filters state:", filters);
    console.log("🌐 API Query Params:", queryParams.toString());

    const url = `${BASE_URL}/documents/received?${queryParams.toString()}&worker_id=${workerId}`;
    console.log("📡 Fetching URL:", url);

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
    });

    const data = await res.json();
    console.log("📂 API Raw Response:", data);

    if (!res.ok) throw new Error("Failed to fetch documents");

    const mapped = (data?.data?.documents || []).map((doc) => ({
      id: doc.id,
      category: doc.category,
      documentName: doc.name,
      status: doc.status,
      description: doc.description,
      fileType: doc.file_type,
      createdAt: doc.uploaded_at,
    }));

    console.log("✅ Mapped Rows:", mapped);

    setRows(mapped);
    setTotalPages(data?.data?.pagination?.pages || 1);
  } catch (err) {
    console.error("❌ Error fetching documents:", err);
  } finally {
    setLoading(false);
    setTableLoading(false);
  }
};


    fetchDocs();
  }, [decryptedToken, page, limit, search, filters,sortBy, sortOrder]);
  const handleSort = (column) => {
  if (sortBy === column) {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  } else {
    setSortBy(column);
    setSortOrder("asc"); // default asc when new column is clicked
  }
};

  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !rows.some((r) => r.id === id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...rows.map((r) => r.id)])));
    }
  };
  const toggleOne = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );



  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        border: "2px solid #091E4224",
        py: 2,
        px: 0,
        ml: 1,
        mr: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          px: 2,
          borderBottom: "2px solid #091E4224",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontWeight: 600,
              fontSize: "18px",
              color: "#003149",
              textAlign: "left",
            }}
          >
            {t("documentsTable.received.heading") || "Received Documents"}
          </CustomText>
          {tableLoading && <CircularProgress size={20} sx={{ color: "#006EC2" }} />}
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Box
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: 1,
              height: 34,
              border: "2px solid #091E4224",
              display: "flex",
              alignItems: "center",
              minWidth: 250,
              maxWidth: 320,
            }}
          >
            <InputBase
  placeholder={t("documentsTable.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                fontSize: 14,
                fontFamily: "Poppins, sans-serif",
                color: "#939393",
                width: "100%",
              }}
            />
            <SearchIcon sx={{ fontSize: 20, color: "#888", ml: 1 }} />
          </Box>


        </Box>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
          <CircularProgress size={40} sx={{ color: "#006EC2" }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5 }}>
          <img src={empty} alt="No Data" style={{ width: 200, marginBottom: 16 }} />
          <Typography sx={{ fontSize: "15px",        fontFamily: 'Poppins, sans-serif',
 }}>
  {t("documentsTable.received.empty")}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ overflowX: "auto", width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {/* <TableCell sx={headerStyle}>ID</TableCell>
                   */}
           <TableCell
      sx={{ ...headerStyle, cursor: "pointer" }}
      onClick={() => handleSort("id")}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {t("documentsTable.th.id")}

        {sortBy === "id" ? (
          sortOrder === "asc" ? (
            <ArrowUpwardIcon fontSize="small" sx={{fontSize:"12px",}} />
          ) : (
            <ArrowDownwardIcon fontSize="small" sx={{fontSize:"12px"}} />
          )
        ) : (
          <ArrowDownwardIcon fontSize="small" sx={{fontSize:"12px"}} /> // default faint icon
        )}
      </Box>
    </TableCell>
                  <TableCell sx={headerStyle}>{t("documentsTable.th.category")}</TableCell>
                  {/* <TableCell sx={headerStyle}>Document Name</TableCell> */}
                      <TableCell
      sx={{ ...headerStyle, cursor: "pointer" }}
      onClick={() => handleSort("department_name")}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {t("documentsTable.th.departmentName")}
        {sortBy === "department_name" ? (
          sortOrder === "asc" ? (
            <ArrowUpwardIcon fontSize="small" sx={{fontSize:"12px"}}/>
          ) : (
            <ArrowDownwardIcon fontSize="small" sx={{fontSize:"12px"}} />
          )
        ) : (
          <ArrowDownwardIcon fontSize="small" sx={{fontSize:"12px"}}/>
        )}
      </Box>
    </TableCell>

    <TableCell sx={headerStyle}>{t("documentsTable.th.description")}</TableCell>
    <TableCell sx={headerStyle}>{t("documentsTable.th.status")}</TableCell>
    <TableCell sx={headerStyle}>{t("documentsTable.th.fileType")}</TableCell>
                  {/* <TableCell sx={headerStyle}>Date - Time</TableCell>
                   */}
                   
    <TableCell
      sx={{ ...headerStyle, cursor: "pointer" }}
      onClick={() => handleSort("uploaded_at")}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {t("documentsTable.th.dateTime") || t("common.date") || "Date"}
        {sortBy === "uploaded_at" ? (
          sortOrder === "asc" ? (
            <ArrowUpwardIcon fontSize="small" sx={{fontSize:"12px"}} />
          ) : (
            <ArrowDownwardIcon fontSize="small" sx={{fontSize:"12px"}} />
          )
        ) : (
          <ArrowDownwardIcon fontSize="small" sx={{fontSize:"12px"}} />
        )}
      </Box>
    </TableCell>
   
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={dataStyle}>{row.id}</TableCell>
                    <TableCell sx={dataStyle}>{row.category}</TableCell>
                    <TableCell sx={dataStyle}>
                      <TruncatedCell text={row.documentName} />
                    </TableCell>
                    <TableCell sx={dataStyle}>
                      <TruncatedCell text={row.description} />
                    </TableCell>
                    <TableCell sx={dataStyle}>
                      <Box sx={{ pointerEvents: "none" }}>
                        <StatusDropdown currentStatus={row.status} />
                      </Box>
                    </TableCell>
                    <TableCell sx={dataStyle}>
                      <Tooltip title={(row.fileType || "").toUpperCase()}>
                        <Box
                          component="img"
                          src={fileTypeIcon(row.fileType)}
                          alt={row.fileType || "file"}
                          sx={{ width: 22, height: 22, display: "block" }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={dataStyle}>{formatDate(row.createdAt)}</TableCell>
               
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                shape="rounded"
                color="primary"
                size="small"
              />
            </Box>
          </Box>

    
        </>
      )}


    </Box>
  );
}